import {computed, Signal, signal} from '@angular/core';

import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {AksjeeierEnkelModell, AksjeklasseModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {dekodIntervallerStreng, erGyldigIntervallerstreng, finnOverlappendeMellom, hullIIntervaller, inneholderNegative, inneholderOverlapper, Intervall, intervallerSomStreng, normaliserIntervaller, summerIntervaller} from "felles-ng2/typer/aksjenummer";
import {erIkkeTomStreng, erTomEllerNil} from "felles/stoettefunk/strenger";
import {LukkAbonnementer} from "felles-ng2/infrastruktur/rxjs/LukkAbonnementer";
import {objectify} from "radash";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";

const debug = initDebugger('BeholdningerModellBase');

export abstract class BeholdningerModellBase<T extends BeholdningModellBase> {
    beholdninger: T[] = [];

    totaltAntallAksjer = computed(() => this.beholdninger.map(beholdning => beholdning.antallAksjer()).reduce((a, b) => a + b, 0));
    totaltAntallAksjerPerAksjeklasse = computed(() => this.antallAksjerPerAksjeklasse());
    aksjenummerIBruk = computed(() => normaliserIntervaller(this.alleAksjenummer()));
    aksjenummerIkkeIBruk = computed(() => hullIIntervaller(this.alleAksjenummer()));
    

    constructor(aksjeeiere: AksjeeierEnkelModell[], public readonly ab: LukkAbonnementer, sortering = Aksjeeiersortering.uendret, public readonly oversettelser:Oversettelser) {
       this.initBeholdninger(aksjeeiere, sortering);
    }

    protected initBeholdninger(aksjeeiere: AksjeeierEnkelModell[], sortering: Aksjeeiersortering) {
        this.beholdninger = aksjeeiere
          .map(aksjeeier => this.nyBeholdningModell(aksjeeier, this))
          .sort((a, b)=> a.sammenliknMed(b, sortering));
    }

    protected abstract nyBeholdningModell(aksjeeier: AksjeeierEnkelModell, forelder: BeholdningerModellBase<T>): T;

    private alleAksjenummer() {
        return this.beholdninger.flatMap(beholdning => beholdning.aksjenummerintervaller());
    }

    /** Retunerer en map med ett innslag for hver aksjeklasse i bruker, med antall aksjer som verdi.
     * Beholdninger ikke knyttet til noen aksjeklasse ligger under nøkkelen {@link tomAksjeklasseModell} */
    private antallAksjerPerAksjeklasse() {
       const beholdninger = this.beholdninger
         .map(beholdning  => ({aksjeklasse: beholdning.aksjeklasse(), antallAksjer: beholdning.antallAksjer()}));
       return antallAksjerPerAksjeklasse(beholdninger,this.oversettelser);
    }
}


export abstract class BeholdningModellBase {
    aksjeeier: AksjeeierEnkelModell;
    /** Siste gyldige aksjenummerinteval, slik dette er def på aksjeeier eller i skjemafelt for redigering av aksjenummer.
     * Merk at samlingen ikke nødvendigvis er normalisert.
     * NB bruk {@link #setAksjenummerintervaller} for å sette verdi! */
    aksjenummerintervaller = signal<Intervall[]>([]);
    antallAksjer: Signal<number>;
    aksjeklasse = signal<AksjeklasseModell|null>(null);
    antallAksjerDifferanse: Signal<number>;
    eierandel: Signal<number>;

    protected forelder: BeholdningerModellBase<BeholdningModellBase>;

    constructor(aksjeeier: AksjeeierEnkelModell, forelder: BeholdningerModellBase<BeholdningModellBase>) {
        this.forelder = forelder;
        this.aksjeeier = aksjeeier;

        this.antallAksjer = computed(() => summerIntervaller(this.aksjenummerintervaller()));
        this.antallAksjerDifferanse = computed(() => this.antallAksjer() - (this.aksjeeier.naavaerendeBeholdning?.antall ?? 0));
        this.eierandel = computed(() => this.antallAksjer() / this.forelder.totaltAntallAksjer());

        //starter med verdier satt på angitt aksjeeier, ved bruk i skjema bør aksjenummer/klasse-feltet sørge for å dytte endringer til disse signalene
        this.aksjeklasse.set(this.aksjeeier.naavaerendeBeholdning?.aksjeklasse ?? null);
        this.setAksjenummerintervaller(this.aksjeeier.naavaerendeBeholdning?.aksjenummer ?? null);
    }

    /** Gjeldende aksjenummerverdi, slik denne er satt på aksjeeier eller i skjema ved bruk i skjemasammenheng.
     * MERK at dette er en potensielt ikke-gyldig/ikke-velformatert verdi.
     * Dette i motsetning til {@link #aksjenummerintervaller} som gir tilgang til siste gyldige/velformaterte verdi. */
    abstract aksjenummerFeltverdi(): string;

    /** returnerer negativt tall dersom denne beholdningen sorterer før angitt `annen`, 0 dersom like, positivt dersom denne sorterer etter.
     * Hva det sorteres på defineres via angitt `sortering`*/
    sammenliknMed(annen: BeholdningModellBase, sortering: Aksjeeiersortering): number {
        switch (sortering){
            case Aksjeeiersortering.uendret: return 1;

            case Aksjeeiersortering.etterBeholdning:
                if (erNil(annen)) return -1;
                let likhet = (this.førsteAksjenummer() ?? 0) - (annen.førsteAksjenummer() ?? 0);
                if (likhet == 0) {
                    likhet = this.aksjeeier.navn.toLocaleLowerCase().localeCompare(annen.aksjeeier.navn.toLocaleLowerCase());
                    if (likhet == 0) likhet = this.aksjeeier.id.localeCompare(annen.aksjeeier.id);
                }
                return likhet;
                break;

            default: return -1;
        }
    }

    //hjelpemetoder

    /** Setter aksjenummerintervaller Signal verdi, dekoder angitt streng til en liste med {@link Intervall} instanser,
     * sortert i stigende rekkefølge etter {@link Intervall#start}.
     * For intern bruk. */
    protected setAksjenummerintervaller(aksjenummer: string | null) {
        this.aksjenummerintervaller.set(dekodIntervallerStreng(aksjenummer));
    }

    /** Returnerer minste aksjenummer til denne beholdningen, null dersom ingen beholdning er registrert. */
    protected førsteAksjenummer():number |null{
        const intervaller = this.aksjenummerintervaller();
        return intervaller.length > 0 ? intervaller[0].start : null;
    }

    protected validerAksjenummer(aksjenummer: string): Valideringsfeil | null {
        //debug("validerAkjsenummer", aksjenummer, this.aksjeeier.navn)
        if (erTomEllerNil(aksjenummer)) return null; //vi må tollere aksjonærer uten noen aksjer

        //egen melding dersom bruker har tastet 0
        if (/^\s*0\s*$/.test(aksjenummer)) return {talletNull: this.talletNullSomBrukerrettetStreng()};

        if (!erGyldigIntervallerstreng(aksjenummer)) return {ugyldigFormat: this.ugyldigFormatSomBrukerrettetStreng(aksjenummer)};

        const feltverdi = dekodIntervallerStreng(aksjenummer);

        const negative = this.beregnNegativeSomBrukerrettetStreng(feltverdi);
        if (erIkkeTomStreng(negative)) return {negative};

        const overlapper = this.beregnOverlapperSomBrukerrettetStreng(feltverdi);
        if (erIkkeTomStreng(overlapper)) return {overlapper};

        //subscribe på aksjenummerSkjemakontroller.statusChanges i forelder sørger for at motsvarende rader (dvs annen) også blir markert som ugyldige
        const overlapperAnnen = this.beregnOverlapperMedAnnenBeholdningSomBrukerrettetStreng(feltverdi);
        if (erIkkeTomStreng(overlapperAnnen)) return {overlapperAnnen};

        return null;
    }

    private ugyldigFormatSomBrukerrettetStreng(aksjenummer:string){
        return this.forelder.oversettelser.oversett(i18n("'{aksjenummer}' har ugyldig format, aksjenummer består av tall, separert med komma eller strek"), {aksjenummer: aksjenummer});
    }

    private talletNullSomBrukerrettetStreng(){
        return this.forelder.oversettelser.oversett(i18n('Dersom aksjonæren ikke har noen aksjer lar du feltet være blankt, 0 er ikke et gyldig aksjenummer'));
    }

    private beregnNegativeSomBrukerrettetStreng(feltverdi: Intervall[]) {
        return inneholderNegative(feltverdi)
          .map(negativt => {
              const interval = intervallerSomStreng(negativt);
              return this.forelder.oversettelser.oversett(i18n("{interval} er negativt, start må være mindre enn slutt"), {interval: interval})
          })
          .join(', ');
    }

    private beregnOverlapperSomBrukerrettetStreng(feltverdi: Intervall[]) {
        //merk at vi finner kun første overlapp - det kan være flere..
        return inneholderOverlapper(feltverdi)
          .map(overlapp => {
              const a = intervallerSomStreng(overlapp.intervall);
              const b = overlapp.overlapperMed.map(intevall => intervallerSomStreng(intevall)).join(', ');
              return this.forelder.oversettelser.oversett(i18n("{a} overlapper med {b}"), {a: a, b: b})
          })
          .join(', ');
    }

    private beregnOverlapperMedAnnenBeholdningSomBrukerrettetStreng(feltverdi: Intervall[]) {
        return this.beregnOverlapperMedAnnenBeholdning(feltverdi)
          .map(overlapp => {
              const navn = overlapp.annen.aksjeeier.navn;
              const intervallerDenne = overlapp.overlapp.map(overlapp => intervallerSomStreng(overlapp.a)).join(', ')
              const intervallerAnnen = overlapp.overlapp.flatMap(overlapp => overlapp.overlapper_b).map(intevall => intervallerSomStreng(intevall)).join(', ')
              return this.forelder.oversettelser.oversett(i18n("{intervallerDenne} overlapper med {intervallerAnnen} i {navn}"), {intervallerDenne: intervallerDenne, intervallerAnnen: intervallerAnnen, navn: navn})
          })
          .join(', ');
    }

    private beregnOverlapperMedAnnenBeholdning(feltverdi: Intervall[]) {
        const denneBeholdningen = feltverdi;
        return this.forelder.beholdninger
          .filter(annen => annen !== this) //utelater seg selv fra listen
          .map(annen => {
              const overlapp = finnOverlappendeMellom(denneBeholdningen, dekodIntervallerStreng(annen.aksjenummerFeltverdi()));
              return {annen: annen, overlapp: overlapp};
          })
          .filter(muligOverlapp => muligOverlapp.overlapp.length > 0);
    }
}

export enum Aksjeeiersortering {
    uendret, etterBeholdning
}

export interface Valideringsfeil {
    talletNull?: string;
    ugyldigFormat?: string;
    negative?: string;
    overlapper?: string;
    overlapperAnnen?: string;
}

/** representerer fravære av aksjeklasse, dvs en beholdning der  {@link BeholdningModellBase#aksjeklasse} == null */
 let tomAksjeklasseModellKonstant: AksjeklasseModell|null = null;

export function tomAksjeklasseModell(oversettelser: Oversettelser): AksjeklasseModell {
    // NB! fører til at endring i språk ikke endrer teksten i 'ikke angitt'
    if (tomAksjeklasseModellKonstant == null) {
        tomAksjeklasseModellKonstant = {
            beskrivelse: '',
            id: 'f8df017c-fa48-43de-ae14-cec724d5bd5c',
            navn: oversettelser.oversett(i18n('Ikke angitt'))
        }
    }
    return tomAksjeklasseModellKonstant;
}

/** Retunerer en map med ett innslag for hver aksjeklasse i bruker, med antall aksjer som verdi.
 * Beholdninger ikke knyttet til noen aksjeklasse ligger under nøkkelen {@link tomAksjeklasseModell} */
export function antallAksjerPerAksjeklasse(beholdninger:{aksjeklasse:AksjeklasseModell|null, antallAksjer:number}[], oversettelser:Oversettelser) {
    //må sørge for at beholdning.aksjeklasse mapper til samme instans for like aksjeklasser, for å fungere med Maps SameValueZero key strategi (i returnert Map)
    //klasser er nå et objekt som inneholder ett innslag per aksjeklasse (id -> AksjeklasseModell) referert i beholdninger
    const klasser = objectify(beholdninger.filter(b => erIkkeNil(b.aksjeklasse)),
      beholdning => beholdning.aksjeklasse!.id as string,
      beholdning => beholdning.aksjeklasse)

    const grupper = beholdninger
      .filter(beholdning => beholdning.antallAksjer > 0)
      .reduce(
        (gruppert, beholdning) => {
            //aksjeklasse kan være null - disse knytter vi til tomAksjeklasseModell AksjeklasseModell konstant-instansen
            const aksjeklasse = erIkkeNil(beholdning.aksjeklasse) ?
              klasser[beholdning.aksjeklasse.id] as AksjeklasseModell :
              tomAksjeklasseModell(oversettelser);
            gruppert.set(aksjeklasse, beholdning.antallAksjer + (gruppert.get(aksjeklasse) ?? 0));
            return gruppert;
        },
        new Map<AksjeklasseModell, number>());
    return grupper;
}
